import { useEffect, useState, type FormEvent } from 'react'
import { Navigate } from 'react-router'
import { Button, Eyebrow, Field, Input, Notice } from '@/ui'
import { useAuth } from './AuthProvider'

/** Landing for the magic link. Signs in, then returns to the path the link was requested from (an invite, a deep link) or home. */
export function EmailLinkPage() {
  const { completeEmailLink, user, error } = useAuth()
  const [done, setDone] = useState<boolean | 'email-required' | null>(null)
  const [next, setNext] = useState('/')
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    void completeEmailLink().then((r) => {
      setNext(r.next)
      setDone(r.status)
    })
  }, [completeEmailLink])

  // The link was opened on a different device or browser than it was requested from, so the address
  // isn't remembered here. Ask for it inline rather than with a browser prompt.
  const onConfirm = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    const r = await completeEmailLink(email)
    setNext(r.next)
    setDone(r.status)
    setBusy(false)
  }

  if (user && done === true) return <Navigate to={next} replace />
  return (
    <main id="main" className="grid min-h-dvh place-items-center px-6">
      <div className="grid w-full max-w-sm gap-6">
        {done === 'email-required' ? (
          <form onSubmit={onConfirm} className="grid gap-5">
            <div className="grid gap-2">
              <Eyebrow>One more step</Eyebrow>
              <h1 className="text-h3">Confirm your email.</h1>
              <p className="text-small text-dim">This link was opened somewhere other than where it was requested. Enter the address it was sent to and we’ll finish signing you in.</p>
            </div>
            {error && <Notice tone="danger">{error}</Notice>}
            <Field label="Email">
              <Input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
            </Field>
            <div>
              <Button type="submit" disabled={busy || !email}>{busy ? 'Signing in…' : 'Continue'}</Button>
            </div>
          </form>
        ) : done === false ? (
          <Notice tone="danger">{error ?? 'This link is not valid. Request a new one from the sign-in page.'}</Notice>
        ) : (
          <p className="text-eyebrow uppercase tracking-eyebrow text-dim">Signing you in…</p>
        )}
      </div>
    </main>
  )
}
